<template>
	<div class="gantt-calendar" :style="`width:${calendarViewWidth}px`" ref="calendar">
		<div class="gantt-date">
			<div class="gantt-year-month">
				<div v-for="(calendar, index) in calendars" :key="`${calendar.year}-${index}`">
					<div
						class="calendar-date"
						:style="`width:${calendar.calendar * block_size}px;left:${
							calendar.start_block_number * block_size
						}px`"
					>
						<v-btn
							v-if="index == 0"
							absolute
							left
							icon
							color="white"
							@click="$emit('change-period', { number: -1 })"
							><v-icon>mdi-chevron-left</v-icon></v-btn
						>
						{{ calendar.date }}
						<v-btn
							v-if="index == calendars.length - 1"
							absolute
							right
							icon
							color="white"
							@click="$emit('change-period', { number: 1 })"
							><v-icon>mdi-chevron-right</v-icon></v-btn
						>
					</div>
				</div>
			</div>

			<!-- ここからガントチャート 日付と曜日-->
			<div class="gantt-day">
				<div v-for="(calendar, index) in calendars" :key="`${calendar.year}-${index}`">
					<div v-for="(day, index) in calendar.days" :key="`${calendar.year}-${day}-${index}`">
						<div
							:class="{
								'gantt-days': true,
								clickable: !onlyDateChartType,
								saturday: day.dayOfWeek === '土',
								sunday: day.dayOfWeek === '日',
								today:
									calendar.year === today.year() &&
									calendar.month === today.month() &&
									day.day === today.date(),
								'bg-dark': day.dark,
							}"
							:style="`width:${block_size}px;left:${day.block_number * block_size}px`"
							@click="onlyDateChartType ? false : changeChartType(calendar, day)"
						>
							<span>{{ day.day }}</span>
							<span>{{ day.dayOfWeek }}</span>
						</div>
					</div>
				</div>
			</div>
			<div
				class="gantt-day"
				v-if="topDateUnitHeight > 0"
				:style="{ height: topDateUnitHeight + 'px' }"
			>
				<div v-for="(calendar, index) in calendars" :key="`${calendar.year}-${index}`">
					<div v-for="(day, index) in calendar.days" :key="`${calendar.year}-${day}-${index}`">
						<div
							class="gantt-days"
							:style="`width:${block_size}px;left:${
								day.block_number * block_size
							}px; height:${topDateUnitHeight}px`"
						>
							<slot name="top-date-unit" :date="`${calendar.year}-${calendar.month}-${day.day}`" />
						</div>
					</div>
				</div>
			</div>
			<!-- 日毎の列 -->
			<template v-if="showCells">
				<div style="position: relative">
					<div v-if="topDateUnitHeight" :style="`height: ${topDateUnitHeight}px; top: 0;`"></div>
					<div
						class="gantt-height"
						v-for="n of lists.length"
						:key="n"
						:style="`height:${task_height}px; top: ${task_height * (n - 1) + topDateUnitHeight}px`"
					>
						<div v-for="(calendar, index) in calendars" :key="`${calendar.year}-${index}`">
							<div v-for="(day, index) in calendar.days" :key="`${calendar.year}-${day}-${index}`">
								<div
									class="days-height days-height--cell"
									:class="{
										saturday: day.dayOfWeek === '土',
										sunday: day.dayOfWeek === '日',
										'bg-dark': day.dark,
									}"
									:style="`width:${block_size}px;left:${
										day.block_number * block_size
									}px;height:${task_height}px`"
									@click="startCellDrag(calendar, day)"
								></div>
							</div>
						</div>
					</div>
				</div>
			</template>
			<template v-else>
				<div
					class="gantt-height"
					:style="`height:${task_height * lists.length + topDateUnitHeight}px`"
				>
					<div v-for="(calendar, index) in calendars" :key="`${calendar.year}-${index}`">
						<div v-for="(day, index) in calendar.days" :key="`${calendar.year}-${day}-${index}`">
							<div
								class="days-height"
								:class="{
									saturday: day.dayOfWeek === '土',
									sunday: day.dayOfWeek === '日',
									'bg-dark': day.dark,
								}"
								:style="`width:${block_size}px;left:${day.block_number * block_size}px;height:${
									task_height * lists.length
								}px`"
							></div>
						</div>
					</div>
				</div>
			</template>

			<!-- タスクバー -->
			<div class="gantt-bar-area" :style="`height:${task_height * lists.length}px`">
				<div v-for="(taskLine, index) in taskBars" :key="index">
					<template v-if="taskArrayName != null">
						<template v-for="(task, j) in taskLine.bars">
							<v-hover
								v-if="Object.keys(task).length"
								v-slot="{ hover }"
								close-delay="500"
								open-delay="500"
								:key="j"
							>
								<slot
									name="task-bar"
									:task="task"
									:mouseDownMove="mouseDownMove"
									:mouseDownResize="mouseDownResize"
								>
									<TaskBar
										v-if="taskBarType == 'bar'"
										:task="task"
										:taskBar="taskBar"
										:taskBarStyle="taskBarStyle"
										:hover="hover"
										:editableStatus="isEditable(task)"
										:taskBarNameStyle="taskBarNameStyle"
										:dragging="dragging"
										:taskLine="taskLine"
										:formatDisplayTimeLeft="formatDisplayTimeLeft"
										:formatDisplayTimeRight="formatDisplayTimeRight"
										:isResize="isResize"
										:rightResizing="rightResizing"
										:leftResizing="leftResizing"
										:barClickable="barClickable"
										@click-bar="$emit('click-bar', { task, taskLine })"
										@mouse-down-resize="mouseDownResize(task, taskLine, $event)"
										@mouse-down-move="mouseDownMove(task, taskLine)"
									>
										<template v-slot:hover-card="{ hover, task, hoverPosition, taskLine }">
											<slot
												name="hover-card"
												:hover="hover"
												:task="task"
												:hoverPosition="hoverPosition"
												:taskLine="taskLine"
											/>
										</template>
										<template v-slot:task-name="{ task }">
											<slot name="task-name" :task="task" />
										</template>
									</TaskBar>
									<TaskLine
										v-else-if="taskBarType == 'line'"
										:task="task"
										:taskBar="taskBar"
										:taskBarStyle="taskBarStyle"
										:hover="hover"
										:editableStatus="isEditable(task)"
										:taskBarNameStyle="taskBarNameStyle"
										:dragging="dragging"
										:taskLine="taskLine"
										:formatDisplayTimeLeft="formatDisplayTimeLeft"
										:formatDisplayTimeRight="formatDisplayTimeRight"
										:isResize="isResize"
										:rightResizing="rightResizing"
										:leftResizing="leftResizing"
										:barClickable="barClickable"
										@click-bar="$emit('click-bar', { task, taskLine })"
										@mouse-down-resize="mouseDownResize(task, taskLine, $event)"
										@mouse-down-move="mouseDownMove(task, taskLine)"
									>
										<template v-slot:hover-card="{ hover, task, hoverPosition, taskLine }">
											<slot
												name="hover-card"
												:hover="hover"
												:task="task"
												:hoverPosition="hoverPosition"
												:taskLine="taskLine"
											/>
										</template>
										<template v-slot:task-name="{ task }">
											<slot name="task-name" :task="task" />
										</template>
									</TaskLine>
								</slot>
							</v-hover>
						</template>
					</template>
					<template v-else-if="taskLine.cat == 'task'">
						<v-hover v-slot="{ hover }" close-delay="200" open-delay="200">
							<slot
								name="task-bar"
								:task="taskLine"
								:mouseDownMove="mouseDownMove"
								:mouseDownResize="mouseDownResize"
							>
								<TaskBar
									v-if="taskBarType == 'bar'"
									:task="taskLine"
									:taskBar="taskBar"
									:taskBarStyle="taskBarStyle"
									:hover="hover"
									:editableStatus="isEditable(taskLine)"
									:taskBarNameStyle="taskBarNameStyle"
									:dragging="dragging"
									:taskLine="null"
									:formatDisplayTimeLeft="formatDisplayTimeLeft"
									:formatDisplayTimeRight="formatDisplayTimeRight"
									:isResize="isResize"
									:rightResizing="rightResizing"
									:leftResizing="leftResizing"
									:barClickable="barClickable"
									@click-bar="$emit('click-bar', { task, taskLine })"
									@mouse-down-resize="mouseDownResize(taskLine, null, $event)"
									@mouse-down-move="mouseDownMove(taskLine, null)"
								>
									<template v-slot:hover-card="{ hover, task, hoverPosition, taskLine }">
										<slot
											name="hover-card"
											:hover="hover"
											:task="task"
											:hoverPosition="hoverPosition"
											:taskLine="taskLine"
										/>
									</template>
									<template v-slot:task-name="{ task }">
										<slot name="task-name" :task="task" />
									</template>
								</TaskBar>
								<TaskLine
									v-else-if="taskBarType == 'line'"
									:task="taskLine"
									:taskBar="taskBar"
									:taskBarStyle="taskBarStyle"
									:hover="hover"
									:editableStatus="isEditable(taskLine)"
									:taskBarNameStyle="taskBarNameStyle"
									:dragging="dragging"
									:taskLine="taskLine"
									:formatDisplayTimeLeft="formatDisplayTimeLeft"
									:formatDisplayTimeRight="formatDisplayTimeRight"
									:isResize="isResize"
									:rightResizing="rightResizing"
									:leftResizing="leftResizing"
									:barClickable="barClickable"
									@click-bar="$emit('click-bar', { task, taskLine })"
									@mouse-down-resize="mouseDownResize(taskLine, null, $event)"
									@mouse-down-move="mouseDownMove(taskLine, null)"
								>
									<template v-slot:hover-card="{ hover, task, hoverPosition, taskLine }">
										<slot
											name="hover-card"
											:hover="hover"
											:task="task"
											:hoverPosition="hoverPosition"
											:taskLine="taskLine"
										/>
									</template>
									<template v-slot:task-name="{ task }">
										<slot name="task-name" :task="task" />
									</template>
								</TaskLine>
							</slot>
						</v-hover>
					</template>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import moment from "moment"
import "moment/locale/ja"
import TaskBar from "./TaskBar.vue"
import TaskLine from "./TaskLine.vue"

export default {
	components: {
		TaskBar,
		TaskLine,
	},
	filters: {
		dateFormatter: function (date) {
			if (!date) return ""
			return moment(date).format("YYYY/MM/DD")
		},
	},
	props: {
		// trueであればバーの拡大・縮小・移動が可能
		changeDatetimeFlag: {
			type: Boolean,
			default: false,
		},
		// タスクのデータ
		tasks: {
			type: Array,
			default: () => [],
		},
		categoryNameKey: {
			type: String,
			default: "",
		},
		taskNameKey: {
			type: String,
			default: "",
		},
		startDateKey: {
			type: String,
			default: "",
		},
		endDateKey: {
			type: String,
			default: "",
		},
		startTimeKey: {
			type: String,
			default: "",
		},
		endTimeKey: {
			type: String,
			default: "",
		},
		fontSize: {
			type: String,
			default: "16px",
		},
		changeCategoryTaskFlag: {
			type: Boolean,
			default: false,
		},
		// タスクの開始・完了日を表示するか
		showDates: {
			type: Boolean,
			default: true,
		},
		// 一行に複数タスクバーを表示する場合の参照する行列のキー
		taskArrayName: {
			type: null,
			required: false,
			default: null,
		},
		// タスクバー内のpタグのcss
		taskBarNameStyle: {
			type: Object,
			required: false,
			default: () => ({}),
		},
		// タスクバー用のcss
		taskBarStyle: {
			type: Object,
			required: false,
			default: () => ({}),
		},
		// 背景色を分ける場合はそれを分けるためのキー
		colorRecognitionKey: {
			type: String,
			required: false,
		},
		// colorRecognitionKeyを指定する場合は，colorRecognitionKeyの値をキー、色をバリューの形でオブジェクトで受け取る
		// 例） {pending: "skyblue", ongoing:"orange", completed:"green", canceled:"grey"}
		taskBarColorsByRecognitionKey: {
			type: Object,
			required: false,
		},
		// 編集可能（ドラッグ）を分ける場合はそれを分けるためのキー
		editableRecognitionKey: {
			type: String,
			required: false,
		},
		// editableRecognitionKeyを指定する場合は，editableRecognitionKeyの値をキー、色をバリューの形でオブジェクトで受け取る
		// 例） {pending: true, ongoing:true, completed:false, canceled:false}
		editableByRecognitionKey: {
			type: Object,
			required: false,
		},
		taskBarType: {
			type: String,
			required: false,
			default: "bar",
		},
		task_width: { type: Number, required: true },
		task_height: { type: Number, required: true },
		start_month: {
			type: String,
			required: true,
			default: moment().add(-5, "months").startOf("month").format("YYYY-MM-DD"),
		},
		end_month: {
			type: String,
			required: true,
			default: moment().add(6, "months").endOf("month").format("YYYY-MM-DD"),
		},
		lists: {
			type: Array,
			required: true,
		},
		block_size: {
			type: Number,
			required: false,
			default: 30,
		},
		// 日付下のユニットの高さ 指定するとユニットを表示
		topDateUnitHeight: {
			type: Number,
			required: false,
			default: 0,
		},
		// 日毎のガントチャートだけを表示する場合
		onlyDateChartType: {
			type: Boolean,
			required: false,
			default: false,
		},
		darkColorUntil: {
			type: String,
			required: false,
		},
		darkColorFrom: {
			type: String,
			required: false,
		},
		// セルを表示するかどうか
		showCells: {
			type: Boolean,
			required: false,
			default: false,
		},
		barClickable: {
			type: Boolean,
			required: false,
			default: false,
		},
	},
	data() {
		return {
			block_number: 0,
			calendars: [],
			inner_width: 0,
			inner_height: 0,
			today: moment(),
			position_id: 0,
			dragging: false,
			pageX: "",
			element: "",
			left: "",
			task_id: "",
			taskBar: null,
			taskLine: null,
			width: "",
			leftResizing: false,
			rightResizing: false,
			task: "",
			dialog: false,
			times: [],
			isResize: false,
			dragging: false,
			menu: false,
			diff: null,
			isShowDisplayTime: false,
			displayTimeLeft: null,
			displayTimeRight: null,
		}
	},

	methods: {
		changeChartType(calendar, day) {
			const date = `${calendar.year}-${calendar.month + 1}-${day.day}`
			this.$emit("change-chart-type", date)
		},
		getDays(year, month, block_number) {
			const dayOfWeek = ["日", "月", "火", "水", "木", "金", "土"]
			let days = []
			let date = moment(`${year}-${month}-01`)
			// 今月の日数を取得
			let num = date.daysInMonth()
			const darkColorUntil = this.darkColorUntil ? moment(this.darkColorUntil) : null
			const darkColorFrom = this.darkColorFrom ? moment(this.darkColorFrom) : null
			for (let i = 0; i < num; i++) {
				days.push({
					day: date.date(),
					dayOfWeek: dayOfWeek[date.day()],
					dark: this.isDark(date, darkColorUntil, darkColorFrom),
					block_number,
				})
				date.add(1, "day")
				block_number++
			}
			return days
		},
		isDark(date, darkColorUntil, darkColorFrom) {
			if (!darkColorUntil && !darkColorFrom) return false
			if (darkColorUntil && darkColorFrom) {
				return !date.isBetween(darkColorUntil, darkColorFrom, "day", "[]")
			} else if (darkColorUntil) {
				return date.isAfter(darkColorUntil, "day")
			} else if (darkColorFrom) {
				return date.isBefore(darkColorFrom, "day")
			}
		},
		isEditable(task) {
			if (!this.changeDatetimeFlag || task.lock_flag) return false
			if (this.editableRecognitionKey) {
				let recognitionKey = task[this.editableRecognitionKey]
				return this.editableByRecognitionKey[recognitionKey]
			} else {
				return true
			}
		},
		getCalendar() {
			this.calendars = []
			let block_number = 0
			let days
			let start_month = moment(this.start_month)
			let end_month = moment(this.end_month)
			let between_month = end_month.diff(start_month, "months")
			for (let i = 0; i <= between_month; i++) {
				days = this.getDays(start_month.year(), start_month.format("MM"), block_number)
				this.calendars.push({
					date: start_month.format("YYYY年MM月"),
					year: start_month.year(),
					month: start_month.month(),
					start_block_number: block_number,
					calendar: days.length,
					days: days,
				})
				start_month.add(1, "months")
				block_number = days[days.length - 1].block_number
				block_number++
			}
			return block_number
		},
		getWindowSize() {
			this.$nextTick(() => {
				this.inner_width = window.innerWidth
				this.inner_height = window.innerHeight
			})
		},
		mouseDownMove(task, taskLine) {
			if (this.changeDatetimeFlag) {
				this.dragging = true
				this.pageX = event.pageX
				this.element = event.target.parentElement.parentElement
				this.left = event.target.parentElement.parentElement.style.left
				this.taskBar = task
				this.taskLine = taskLine
				this.displayTimeLeft = this.startTime()
				this.displayTimeRight = this.endTime()
			}
		},
		mouseMove() {
			if (this.dragging) {
				let diff = this.pageX - event.pageX
				this.showDisplayTime(diff)
				let date_from = moment(this.displayTimeLeft)
				let date_to = moment(this.displayTimeRight)

				if (moment(this.start_month).startOf("day").isSameOrAfter(date_from)) {
					date_from = moment(this.start_month).startOf("day")
					this.element.style.left = "0px"
				} else {
					let left = date_from.diff(this.start_month, "days")
					this.element.style.left = `${left * this.block_size}px`
				}
				if (moment(this.end_month).add(1, "d").startOf("day").isSameOrBefore(date_to)) {
					date_to = moment(this.end_month).add(1, "d").startOf("day")
				}

				let between = date_to.diff(date_from, "days")
				between++
				this.element.style.width = `${this.block_size * between}px`
			}
		},
		stopDrag(event) {
			if (this.isResize || this.dragging) {
				let task
				let taskLine
				if (this.taskArrayName) {
					taskLine = this.tasks.find((t) => t.id === this.taskLine.id)
					task = taskLine[this.taskArrayName].find((t) => t.id === this.taskBar.id)
				} else {
					task = this.lists.find((t) => t.id === this.taskBar.id && t.cat == "task")
				}
				if (this.leftResizing || this.dragging) {
					if (task[this.startDateKey] == moment(this.displayTimeLeft).format("YYYY-MM-DD")) {
						this.element.style.left = this.taskBar.style.left
						this.element.style.width = this.taskBar.style.width
					}
					task[this.startDateKey] = moment(this.displayTimeLeft).format("YYYY-MM-DD")
				}
				if (this.rightResizing || this.dragging) {
					if (task[this.endDateKey] == moment(this.displayTimeRight).format("YYYY-MM-DD")) {
						this.element.style.left = this.taskBar.style.left
						this.element.style.width = this.taskBar.style.width
					}
					task[this.endDateKey] = moment(this.displayTimeRight).format("YYYY-MM-DD")
				}
				this.$emit("change-datetime", {
					[this.startDateKey]: task[this.startDateKey],
					[this.endDateKey]: task[this.endDateKey],
					id: task.id,
				})
			}

			this.isResize = false
			this.dragging = false
			this.leftResizing = false
			this.rightResizing = false
			this.taskLine = null
			this.taskBar = null
		},
		startCellDrag(calendar, day) {
			// TODO: セルのドラッグ
		},
		mouseDownResize(task, taskLine, direction) {
			this.isResize = true
			direction === "left" ? (this.leftResizing = true) : (this.rightResizing = true)
			this.pageX = event.pageX
			this.width = event.target.parentElement.parentElement.style.width
			this.left = event.target.parentElement.parentElement.style.left
			this.element = event.target.parentElement.parentElement
			this.taskBar = task
			this.taskLine = taskLine
			if (direction == "left") {
				this.displayTimeLeft = this.startTime()
			} else if (direction == "right") {
				this.displayTimeRight = this.endTime()
			}
		},
		mouseResize() {
			if (this.leftResizing) {
				let diff = this.pageX - event.pageX
				if (parseInt(this.width.replace("px", "")) + diff > this.block_size) {
					this.element.style.width = `${parseInt(this.width.replace("px", "")) + diff}px`
					this.element.style.left = `${this.left.replace("px", "") - diff}px`
					this.showDisplayTime(diff)
				}
			}
			if (this.rightResizing) {
				let diff = this.pageX - event.pageX
				if (parseInt(this.width.replace("px", "")) - diff > this.block_size) {
					this.element.style.width = `${parseInt(this.width.replace("px", "")) - diff}px`
					this.showDisplayTime(diff)
				}
			}
		},
		showDisplayTime(diff) {
			const days = this.calculateTime(diff)
			if (this.leftResizing || this.dragging) {
				this.displayTimeLeft = moment(this.startTime()).add(-days, "d").format("YYYY-MM-DD")
			}
			if (this.rightResizing || this.dragging) {
				this.displayTimeRight = moment(this.endTime()).add(-days, "d").format("YYYY-MM-DD")
			}
		},
		calculateTime(diff) {
			this.diff = diff
			const DIGIT = 1
			const INIT_NUM = 1
			const INTERVAL = 1
			let times = parseFloat(this.diff / this.block_size).toFixed(DIGIT)
			let days
			if (this.rightResizing) {
				days = Math.floor(times / INIT_NUM) * INTERVAL
			} else if (this.leftResizing) {
				days = Math.ceil(times / INIT_NUM) * INTERVAL
			} else if (this.dragging) {
				days = Math.ceil(times / INIT_NUM) * INTERVAL
			}
			return days
		},
		startTime() {
			return this.taskBar[this.startDateKey]
				? this.taskBar[this.startDateKey]
				: this.taskBar[this.endDateKey]
		},
		endTime() {
			return this.taskBar[this.endDateKey]
				? this.taskBar[this.endDateKey]
				: this.taskBar[this.startDateKey]
		},
	},
	computed: {
		calendarViewWidth() {
			return this.inner_width - this.task_width
		},
		taskBars() {
			let start_date = moment(this.start_month)
			let top = 10
			let left
			let between
			let start
			let style
			let sectionHeight = 40
			return this.lists.map((task, i) => {
				if (task.cat != "task") return task
				if (this.taskArrayName) {
					task.bars = task[this.taskArrayName]
						.map((t) => {
							let rightDot = false
							let leftDot = false
							style = {}
							if (t[this.startDateKey]) {
								let date_from = moment(t[this.startDateKey])
								let date_to = moment(t[this.endDateKey] ? t[this.endDateKey] : t[this.startDateKey])
								if (
									date_from.isSameOrAfter(this.end_month) ||
									date_to.isSameOrBefore(this.start_month)
								) {
									return null // カレンダーの範囲外ならnull
								}
								if (date_from.isBetween(this.start_month, this.end_month, null, "[]")) {
									leftDot = true
								} else if (date_from.isSameOrBefore(this.start_month)) {
									date_from = moment(this.start_month)
								}
								if (date_to.isBetween(this.start_month, this.end_month, null, "[]")) {
									rightDot = true
								} else if (date_to.isSameOrAfter(this.end_month)) {
									date_to = moment(this.end_month)
								}
								between = date_to.diff(date_from, "days")
								between++
								start = date_from.diff(start_date, "days")
								left = start * this.block_size
								style = {
									top: `${top + i * sectionHeight}px`,
									left: `${left}px`,
									width: `${this.block_size * between}px`,
								}
								// colorRecognitionKeyによる色の変更
								if (this.colorRecognitionKey) {
									let colorKey = t[this.colorRecognitionKey]
									if (this.taskBarType == "bar") {
										style.backgroundColor = this.taskBarColorsByRecognitionKey[colorKey]
									} else if (this.taskBarType == "line") {
										style.borderColor = this.taskBarColorsByRecognitionKey[colorKey]
									}
								}
							} else {
								return null
							}
							return {
								style,
								rightDot,
								leftDot,
								...t,
							}
						})
						.filter((v) => v) // null削除
					return task
				} else {
					let rightDot = false
					let leftDot = false
					style = {}
					if (task[this.startDateKey]) {
						let date_from = moment(task[this.startDateKey])
						let date_to = moment(
							task[this.endDateKey] ? task[this.endDateKey] : task[this.startDateKey]
						)
						if (
							date_from.isSameOrAfter(this.end_month) ||
							date_to.isSameOrBefore(this.start_month)
						) {
							return {} // カレンダーの範囲外ならnull
						}
						if (date_from.isBetween(this.start_month, this.end_month, null, "[]")) {
							leftDot = true
						} else if (date_from.isSameOrBefore(this.start_month)) {
							date_from = moment(this.start_month)
						}
						if (date_to.isBetween(this.start_month, this.end_month, null, "[]")) {
							rightDot = true
						} else if (date_to.isSameOrAfter(this.end_month)) {
							date_to = moment(this.end_month)
						}
						between = date_to.diff(date_from, "days")
						between++
						start = date_from.diff(start_date, "days")
						left = start * this.block_size
						style = {
							top: `${top + i * sectionHeight}px`,
							left: `${left}px`,
							width: `${this.block_size * between}px`,
						}
						// colorRecognitionKeyによる色の変更
						if (this.colorRecognitionKey) {
							let colorKey = task[this.colorRecognitionKey]
							if (this.taskBarType == "bar") {
								style.backgroundColor = this.taskBarColorsByRecognitionKey[colorKey]
							} else if (this.taskBarType == "line") {
								style.borderColor = this.taskBarColorsByRecognitionKey[colorKey]
							}
						}
					} else {
						return {}
					}
					return {
						style,
						rightDot,
						leftDot,
						...task,
					}
				}
			})
		},
		formatDisplayTimeLeft() {
			return moment(this.displayTimeLeft).format("YYYY/MM/DD")
		},
		formatDisplayTimeRight() {
			return moment(this.displayTimeRight).format("YYYY/MM/DD")
		},
	},
	mounted() {
		this.getCalendar()
		window.addEventListener("resize", this.getWindowSize)
		window.addEventListener("mousemove", this.mouseMove)
		window.addEventListener("mouseup", this.stopDrag)
		window.addEventListener("mousemove", this.mouseResize)
		this.$nextTick(() => {
			this.getWindowSize()
		})
	},
	watch: {
		start_month(newVal, _) {
			this.start_month = newVal
			this.getCalendar()
		},
		end_month(newVal, _) {
			this.end_month = newVal
			this.getCalendar()
		},
		deep: true,
	},
}
</script>

<style scoped lang="scss">
@import "../../../stylesheet/shared/color.scss";

.task-name {
	padding: 4px;
	display: flex;
	border-right: solid 1px #80808045;
	align-items: center;
	padding-left: 16px;
	// width: 12rem;
	font-size: 15px;
	justify-content: center;
}
.gantt-calendar {
	overflow-x: scroll;
	overflow-y: hidden;
	border-left: solid 1px #80808045;
}

.gantt-year-month {
	position: relative;
	height: 2rem;
}
.gantt-date {
	// height: 5rem;
	position: relative;
}

.calendar-date {
	position: absolute;
	background: $main-color;
	color: white;
	font-weight: bold;
	border-bottom: solid 1px #80808045;
	border-right: solid 1px #80808045;
	border-top: solid 1px #80808045;
	display: flex;
	align-items: center;
	justify-content: center;
	height: 2rem;
	button {
		z-index: 1;
	}
}
.gantt-day {
	position: relative;
	height: 3rem;
}
.gantt-days {
	position: absolute;
	border-bottom: solid 1px #80808045;
	border-right: solid 1px #80808045;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: bold;
	height: 3rem;
	font-size: 0.8rem;
	flex-direction: column;
}
.saturday {
	background: #dbeafe;
}
.sunday {
	background: #fee2e2;
}
.today {
	background: #dc2626;
	color: white;
}
.gantt-height {
	position: absolute;
}
.days-height {
	position: absolute;
	border-bottom: solid 1px #80808045;
	border-right: solid 1px #80808045;
	&--cell {
		&:hover {
			background: whitesmoke;
		}
	}
}
.gantt-bar-area {
	position: relative;
}

.gantt-bar {
	position: absolute;
	background: orange;
	height: 20px;
	border-radius: 10px;
	&--true {
		cursor: grab;
	}
	&__name {
		overflow: hidden;
		width: 100%;
	}
	&__left {
		top: 6px;
		left: -6px;
		cursor: col-resize;
		position: absolute;
		background: #808080a1;
		height: 8px;
		width: 8px;
	}

	&__right {
		top: 6px;
		right: -6px;
		cursor: col-resize;
		position: absolute;
		background: #808080a1;
		height: 8px;
		width: 8px;
	}
	.display-time-left {
		background: whitesmoke;
		position: absolute;
		padding: 4px;
		font-size: 0.9rem;
		border-radius: 5px;
		bottom: 27px;
		text-align: center;
		opacity: 0.8;
	}

	.display-time-right {
		background: whitesmoke;
		position: absolute;
		padding: 4px;
		font-size: 0.9rem;
		border-radius: 5px;
		bottom: 27px;
		text-align: center;
		opacity: 0.8;
		right: 6px;
	}
}

.gantt-bar-task {
	position: absolute;
	background: orange;
	border-radius: 10px;
}

.bg-dark {
	background-color: lightgrey;
}
</style>
